// src/pages/FAQ.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search } from 'lucide-react';
import { useCms } from '../../hooks/useCms';

export function FAQ() {
  const { t } = useTranslation('auth');
  const { get } = useCms('faq');
  const parseFaqs = (value: string, fallback: Array<{ q: string; a: string }>) => {
    const items = value.split('\n').map(line => line.split('|').map(part => part.trim())).filter(parts => parts.length >= 2 && parts[0] && parts[1]).map(([q, ...answer]) => ({ q, a: answer.join(' | ') }));
    return items.length ? items : fallback;
  };
  const structuredFaqs = (categoryName: string, count: number) =>
    Array.from({ length: count }, (_, index) => ({
      q: get(`questions.${categoryName}${index + 1}Question`, ''),
      a: get(`questions.${categoryName}${index + 1}Answer`, ''),
    })).filter(item => item.q && item.a);

  const [search, setSearch] = useState('');
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [category, setCategory] = useState<string>('buying');

  // Get categories from translation
  const categories = {
    buying: t('faq.categories.buying'),
    selling: t('faq.categories.selling'),
    account: t('faq.categories.account'),
  };

  // Get FAQ data from translation
  const faqData = {
    buying: structuredFaqs('buying', 4).length ? structuredFaqs('buying', 4) : parseFaqs(get('questions.buying', ''), t('faq.faqs.buying', { returnObjects: true }) as Array<{ q: string; a: string }>),
    selling: structuredFaqs('selling', 4).length ? structuredFaqs('selling', 4) : parseFaqs(get('questions.selling', ''), t('faq.faqs.selling', { returnObjects: true }) as Array<{ q: string; a: string }>),
    account: structuredFaqs('account', 3).length ? structuredFaqs('account', 3) : parseFaqs(get('questions.account', ''), t('faq.faqs.account', { returnObjects: true }) as Array<{ q: string; a: string }>),
  };

  const filtered = Object.entries(faqData).reduce((acc, [cat, items]) => {
    if (!search) return acc;
    const matched = items.filter(i => 
      i.q.toLowerCase().includes(search.toLowerCase()) || 
      i.a.toLowerCase().includes(search.toLowerCase())
    );
    if (matched.length) acc[cat] = matched;
    return acc;
  }, {} as typeof faqData);

  const displayFaqs = search ? filtered : { [category]: faqData[category as keyof typeof faqData] };

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <div className="bg-gradient-to-br from-[#2D323A] to-[#1E2229] text-white py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-4xl text-white mb-3">{get('header.title', t('faq.title'))}</h1>
          <p className="text-white/70 mb-8">{get('header.description', t('faq.subtitle'))}</p>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('search_placeholder')}
              className="w-full pl-12 pr-4 py-3.5 bg-white/10 backdrop-blur border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:border-white/40"
            />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {!search && (
          <div className="flex gap-2 mb-6 justify-center">
            {Object.entries(categories).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setCategory(key)}
                className={`px-5 py-2 rounded-xl text-sm transition-colors ${
                  category === key
                    ? 'bg-[#1E6DFF] text-white'
                    : 'bg-white border border-[#E5E7EB] text-[#2D323A] hover:border-[#1E6DFF]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {Object.keys(displayFaqs).length === 0 && (
          <div className="text-center py-12">
            <p className="text-[#717182]">{t('faq.no_results')} "{search}"</p>
          </div>
        )}

        {Object.entries(displayFaqs).map(([cat, items]) => (
          <div key={cat} className="mb-6">
            {search && <h2 className="text-[#2D323A] mb-3">{categories[cat as keyof typeof categories]}</h2>}
            <div className="space-y-2">
              {items.map((faq, i) => {
                const key = `${cat}-${i}`;
                const isOpen = openItem === key;
                return (
                  <div
                    key={i}
                    className={`bg-white rounded-2xl border transition-all overflow-hidden ${
                      isOpen ? 'border-[#1E6DFF]' : 'border-[#E5E7EB]'
                    }`}
                  >
                    <button
                      onClick={() => setOpenItem(isOpen ? null : key)}
                      className="w-full flex items-center justify-between p-5 text-left"
                    >
                      <span className="text-[#2D323A] pr-4">{faq.q}</span>
                      <ChevronDown
                        className={`w-5 h-5 text-[#717182] flex-shrink-0 transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5">
                        <p className="text-[#717182] leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="bg-[#1E6DFF]/5 border border-[#1E6DFF]/20 rounded-2xl p-6 text-center mt-8">
          <h3 className="text-[#2D323A] mb-2">{t('faq.help_section.title')}</h3>
          <p className="text-[#717182] text-sm mb-4">{t('faq.help_section.description')}</p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors"
          >
            {t('faq.help_section.button')}
          </a>
        </div>
      </div>
    </div>
  );
}
